import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BaseRestDataProvider } from '../data/BaseRestDataProvider';
import { UsersRestDataProvider } from '../data/UsersRestDataProvider';

export interface AuthUser {
  id: string;
  username: string;
  fullName: string;
  role: string;
}

interface AuthContextType {
  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const location = useLocation();

  // Helper to check token expiration
  function isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return false;
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now;
    } catch {
      return true;
    }
  }

  useEffect(() => {
    const token = BaseRestDataProvider.getToken();
    if (token && isTokenExpired(token)) {
      // Don't call logout() directly here, as it triggers a reload and unmounts the component
      BaseRestDataProvider.clearToken();
      setUser(null);
      localStorage.removeItem('cachedUser');
      window.location.reload();
      return;
    }
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));

        // Try to get cached user info
        const cachedUser = localStorage.getItem('cachedUser');
        if (cachedUser) {
          setUser(JSON.parse(cachedUser));
          return;
        }

        // fetch the user role from the server
        var userprovider = new UsersRestDataProvider();
        userprovider.getByEmail(payload.unique_name).then(user => {
          if (user) {
             const userObj = {
              id: user.id || '',
              username: user.username,
              fullName: user.fullName || '',
              role: user.role || 'User',
            };
            setUser(userObj);
            localStorage.setItem('cachedUser', JSON.stringify(userObj));
          } else {
            // If user not found, treat as logged out
            BaseRestDataProvider.clearToken();
            setUser(null);
            localStorage.removeItem('cachedUser');
            window.location.reload();
          }
        });
      } catch {
        // If decoding fails, treat as logged out
        BaseRestDataProvider.clearToken();
        setUser(null);
        localStorage.removeItem('cachedUser');
        window.location.reload();
      }
    } else {
      setUser(null);
    }
  }, [location]);

  const logout = () => {
    BaseRestDataProvider.clearToken();
    setUser(null);
    localStorage.removeItem('cachedUser');
    window.location.reload();
  };

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
